import React from 'react';

import { Alert, AlertTitle, Box } from '@mui/material';
import { isEmpty } from 'es-toolkit/compat';
import { Virtuoso, VirtuosoHandle, VirtuosoProps } from 'react-virtuoso';

import { INITIAL_DATA_CHUNK_SIZE } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { InboxItem, InboxListItemInfoFragment } from 'src/api';
import { InboxItemTypeWithResources } from 'src/constants/inbox';
import { useSearch } from 'src/hooks/common';
import { useInboxFilter, useInboxItems } from 'src/hooks/inboxItem';
import { InboxItemExtended } from 'src/types/inbox';

import { isInboxFormFilterEmpty } from '../inboxFilter/utils';

import { InboxItemCard, InboxItemSkeletonCard, ResourcesInboxItemCard } from './InboxItemCard';
import { InboxItemHotLinkContent } from './InboxItemHotLinkContent';

type ContextProps = {
  loading: boolean;
  inboxItemsCount: number;
  hasSearchParams: boolean;
  hasFilters: boolean;
  hasReachedEnd: boolean;
};

const Footer: React.FC<VirtuosoProps<InboxItem, ContextProps>> = ({ context }) => {
  if (
    !context?.inboxItemsCount &&
    (context?.hasSearchParams || context?.hasFilters) &&
    !context.loading
  ) {
    return (
      <Alert severity="warning">
        <AlertTitle
          sx={{
            marginBottom: '0',
            marginTop: '-4px',
            textTransform: 'uppercase',
          }}
        >
          No inboxes found
        </AlertTitle>
      </Alert>
    );
  }

  if (context?.loading) {
    return Array.from({ length: INITIAL_DATA_CHUNK_SIZE }).map((_el, idx) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: using index as key is acceptable here
      <InboxItemSkeletonCard data-e2e-element="skeleton" key={`inbox-item-skeleton-${idx}`} />
    ));
  }

  return !context?.hasReachedEnd && <InboxItemSkeletonCard />;
};

export const InboxItemsList: React.FC = () => {
  const { filterFormValues } = useInboxFilter();
  const { searchValue } = useSearch();

  const [isReloading, setIsReloading] = React.useState(false);
  const [isHotLink, setIsHotLink] = React.useState(false);

  const hasSearchParams = !isEmpty(searchValue);

  const {
    inboxItems,
    inboxItemsCount,
    onLoadMore,
    loading: inboxItemsLoading,
    hasReachedEnd,
  } = useInboxItems();

  const ref = React.useRef<VirtuosoHandle>(null);

  const handleScrollToTop = React.useCallback(() => {
    ref.current?.scrollToIndex({ index: 0 });
  }, []);

  const handleLoadMore = React.useCallback(
    async (index: number) => {
      const totalCount = index + 1;
      if (totalCount !== inboxItemsCount || inboxItemsCount === 0) {
        if (totalCount === 0) handleScrollToTop();
        const lastItemId = inboxItems[inboxItems.length - 1]?.id;
        if (lastItemId) await onLoadMore(lastItemId);
        setIsReloading(false);
      }
    },
    [handleScrollToTop, inboxItemsCount, onLoadMore, inboxItems],
  );

  const handleLoadFromStart = React.useCallback(async () => {
    handleScrollToTop();
    await onLoadMore();
    setIsReloading(false);
  }, [handleScrollToTop, onLoadMore]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: This useEffect causes cache writes and reads ONLY when Search state changes
  React.useEffect(() => {
    setIsReloading(true);

    if (hasSearchParams) {
      handleLoadMore(-1);
    } else {
      handleLoadFromStart();
    }
  }, [searchValue, filterFormValues]);

  const renderInboxItemCard = (
    _index: unknown,
    inboxItem: Nullable<InboxItemExtended<InboxListItemInfoFragment>>,
  ) => {
    if (!inboxItem) return null;
    if (inboxItem.type === InboxItemTypeWithResources.Resources) {
      return (
        <React.Fragment key={inboxItem.id}>
          <ResourcesInboxItemCard inboxItemId={inboxItem.id} />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment key={inboxItem.id}>
        <InboxItemCard inboxItem={inboxItem} />
      </React.Fragment>
    );
  };

  const isLoading = inboxItemsLoading || isReloading;

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <InboxItemHotLinkContent isHotLink={isHotLink} setIsHotLink={setIsHotLink} />
      <Virtuoso
        ref={ref}
        style={inboxItemsListStyles}
        context={{
          loading: isLoading,
          inboxItemsCount,
          hasSearchParams,
          hasFilters: !isInboxFormFilterEmpty(filterFormValues),
          hasReachedEnd,
        }}
        data={isLoading ? [] : inboxItems}
        onClick={() => {
          setIsHotLink(false);
        }}
        totalCount={inboxItemsCount}
        increaseViewportBy={INITIAL_DATA_CHUNK_SIZE}
        endReached={handleLoadMore}
        components={{ Footer }}
        itemContent={renderInboxItemCard}
      />
    </Box>
  );
};

const inboxItemsListStyles: React.CSSProperties = {
  height: '100%',
  width: '100%',
  overflowY: 'scroll',
};
